interface BannerRefererStorage {
  banner_id: string;
  banner_name: string;
  timestamp: number;
}

export interface BannerRefererParams {
  banner_id: string;
  banner_name: string;
  referer_source: 'banner';
}

const BANNER_REFERER_KEY = 'banner_referer';
const DEFAULT_EXPIRY_MS = 15 * 60 * 1000;
const CLEANUP_EXPIRY_MS = 30 * 60 * 1000;

const removeStoredBannerReferer = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(BANNER_REFERER_KEY);
};

const getStoredBannerData = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.localStorage.getItem(BANNER_REFERER_KEY);
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored) as BannerRefererStorage;
  } catch (error) {
    removeStoredBannerReferer();
    return null;
  }
};

export const getBannerReferer = (
  expiryMs: number = DEFAULT_EXPIRY_MS
): BannerRefererParams | null => {
  const bannerData = getStoredBannerData();
  if (!bannerData) {
    return null;
  }

  const isValid = Date.now() - bannerData.timestamp < expiryMs;
  if (!isValid) {
    removeStoredBannerReferer();
    return null;
  }

  return {
    referer_source: 'banner',
    banner_id: bannerData.banner_id,
    banner_name: bannerData.banner_name,
  };
};

export const setBannerReferer = (
  bannerId: string,
  bannerName: string,
  timestamp: number = Date.now()
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const bannerData: BannerRefererStorage = {
    banner_id: bannerId,
    banner_name: bannerName,
    timestamp,
  };

  window.localStorage.setItem(
    BANNER_REFERER_KEY,
    JSON.stringify(bannerData)
  );
};

export const clearBannerReferer = (): void => {
  removeStoredBannerReferer();
};

export const hasBannerReferer = (expiryMs: number = DEFAULT_EXPIRY_MS) => {
  return getBannerReferer(expiryMs) !== null;
};

export const cleanupExpiredBannerReferer = (
  expiryMs: number = CLEANUP_EXPIRY_MS
) => {
  const bannerData = getStoredBannerData();
  if (!bannerData) {
    return;
  }

  const isExpired = Date.now() - bannerData.timestamp >= expiryMs;
  if (isExpired) {
    removeStoredBannerReferer();
  }
};
